'use client';

import { useEffect, useEffectEvent } from 'react';
import { getBrowserWindow } from '@/lib/utils/browser-window';

type WindowEventFor<Type extends keyof WindowEventMap> = WindowEventMap[Type];

export function useWindowEvent<Type extends keyof WindowEventMap>(
  type: Type,
  listener: (event: WindowEventFor<Type>) => void,
  options?: AddEventListenerOptions | boolean,
  enabled = true
): void {
  const invoke = useEffectEvent(listener);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const target = getBrowserWindow();
    if (!target) {
      return;
    }

    const eventHandler = (event: WindowEventFor<Type>) => {
      invoke(event);
    };

    target.addEventListener(type, eventHandler, options);
    return () => {
      target.removeEventListener(type, eventHandler, options);
    };
  }, [enabled, options, type]);
}
